import React, { useEffect, useRef, useCallback } from 'react';
import { Search, X, Clock, TrendingUp, Loader2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchProductDetails, fetchSimilarProducts, setFilters, fetchProductsByFilters } from "../../../redux/slices/productsSlice";

const Searchbar = () => {
    const [searchTerm, setSearchTerm] = React.useState('');
    const [isOpen, setIsOpen] = React.useState(false);
    const [showSuggestions, setShowSuggestions] = React.useState(false);
    const [suggestions, setSuggestions] = React.useState([]);
    const [recentSearches, setRecentSearches] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(-1);
    
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const debounceRef = useRef(null);

    // Get products from Redux store
    const { products: allProducts } = useSelector((state) => state.products);

    // ===== ENHANCED SEARCH SYSTEM =====
    
    // Synonym mapping for intelligent search
    const synonymMap = {
        // Clothing types
        'tshirt': ['t-shirt', 'tee', 'shirt', 'top'],
        't-shirt': ['tshirt', 'tee', 'shirt', 'top'],
        'tee': ['tshirt', 't-shirt', 'shirt', 'top'],
        'shirt': ['tshirt', 't-shirt', 'tee', 'top'],
        'pants': ['pant', 'trousers', 'bottoms', 'bottom'],
        'pant': ['pants', 'trousers', 'bottoms', 'bottom'],
        'trousers': ['pants', 'pant', 'bottoms'],
        'bottom': ['bottoms', 'pants', 'pant', 'bottomwear'],
        'bottoms': ['bottom', 'pants', 'pant', 'bottomwear'],
        'sweatpant': ['sweatpants', 'joggers', 'track pants', 'sweats'],
        'sweatpants': ['sweatpant', 'joggers', 'track pants', 'sweats'],
        'joggers': ['sweatpants', 'sweatpant', 'track pants'],
        'hoodie': ['hoodies', 'sweatshirt', 'pullover'],
        'hoodies': ['hoodie', 'sweatshirt', 'pullover'],
        'sweatshirt': ['hoodie', 'hoodies', 'pullover'],
        
        // Styles
        'oversized': ['oversize', 'loose', 'baggy', 'relaxed'],
        'oversize': ['oversized', 'loose', 'baggy', 'relaxed'],
        'loose': ['oversized', 'relaxed', 'baggy', 'loose fit'],
        'baggy': ['loose', 'oversized', 'relaxed'],
        'relaxed': ['loose', 'oversized', 'comfortable'],
        'slim': ['fitted', 'tight', 'skinny'],
        'fitted': ['slim', 'tight', 'fit'],
        
        // Categories
        'bottomwear': ['bottom wear', 'bottoms', 'lower wear', 'pants', 'bottom'],
        'topwear': ['top wear', 'tops', 'upper wear', 'top'],
        
        // Patterns/Styles
        'tie dye': ['tiedye', 'tie-dye', 'dye', 'tie'],
        'tiedye': ['tie dye', 'tie-dye', 'dye'],
        'tie': ['tie dye', 'tiedye'],
        'dye': ['tie dye', 'tiedye'],
        'acid wash': ['acidwash', 'acid-wash', 'washed', 'acid'],
        'acidwash': ['acid wash', 'acid-wash'],
        'acid': ['acid wash', 'acidwash'],
        'wash': ['washed', 'acid wash'],
        
        // Use cases
        'casual': ['everyday', 'daily', 'regular'],
        'gym': ['workout', 'fitness', 'exercise', 'athletic'],
        'workout': ['gym', 'fitness', 'exercise'],
        'comfort': ['comfortable', 'cozy', 'soft'],
    };

    // Get all synonyms for a word
    const getSynonyms = (word) => {
        const lowerWord = word.toLowerCase().trim();
        return synonymMap[lowerWord] || [];
    };

    // Simple fuzzy match for typos (Levenshtein distance)
    const fuzzyMatch = (str1, str2, threshold = 2) => {
        const s1 = str1.toLowerCase();
        const s2 = str2.toLowerCase();
        
        if (s1 === s2) return true;
        if (Math.abs(s1.length - s2.length) > threshold) return false;
        
        const matrix = [];
        
        for (let i = 0; i <= s2.length; i++) {
            matrix[i] = [i];
        }
        
        for (let j = 0; j <= s1.length; j++) {
            matrix[0][j] = j;
        }
        
        for (let i = 1; i <= s2.length; i++) {
            for (let j = 1; j <= s1.length; j++) {
                if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j] + 1
                    );
                }
            }
        }
        
        return matrix[s2.length][s1.length] <= threshold;
    };

    // Calculate search relevance score
    const calculateRelevanceScore = (product, query) => {
        const queryLower = query.toLowerCase().trim();
        const queryWords = queryLower.split(/\s+/).filter(w => w.length > 0);
        
        let score = 0;
        const name = (product.name || product.title || '').toLowerCase();
        const category = (product.category || '').toLowerCase();
        const tags = (product.tags || []).map(t => t.toLowerCase());
        const collections = (product.collections || []).map(c => c.toLowerCase());
        const colors = (product.colors || []).map(c => c.toLowerCase());
        const brand = (product.brand || '').toLowerCase();
        const material = (product.material || '').toLowerCase();
        
        // Exact full query matches (highest priority)
        if (name === queryLower) score += 1000;
        if (name.includes(queryLower)) score += 500;
        if (category === queryLower) score += 450;
        if (tags.some(tag => tag === queryLower)) score += 400;
        
        // Check each word in the query
        queryWords.forEach(word => {
            const synonyms = getSynonyms(word);
            const wordsToCheck = [word, ...synonyms];
            
            wordsToCheck.forEach(checkWord => {
                // Split text into words for exact word matching
                const nameWords = name.split(/[\s-_]+/);
                const categoryWords = category.split(/[\s-_]+/);
                
                // Name matches - word by word
                if (nameWords.some(w => w === checkWord)) score += 150;
                if (nameWords.some(w => w.includes(checkWord) && checkWord.length >= 3)) score += 100;
                if (name.includes(checkWord) && checkWord.length >= 3) score += 80;
                
                // Exact tag match
                if (tags.some(tag => tag === checkWord)) score += 120;
                if (tags.some(tag => tag.includes(checkWord) && checkWord.length >= 3)) score += 80;
                
                // Category match
                if (categoryWords.some(w => w === checkWord)) score += 130;
                if (categoryWords.some(w => w.includes(checkWord) && checkWord.length >= 3)) score += 90;
                if (category.includes(checkWord) && checkWord.length >= 3) score += 70;
                
                // Collection match
                if (collections.some(col => col === checkWord)) score += 110;
                if (collections.some(col => col.includes(checkWord) && checkWord.length >= 3)) score += 70;
                
                // Color match
                if (colors.some(color => color === checkWord)) score += 60;
                if (colors.some(color => color.includes(checkWord) && checkWord.length >= 3)) score += 40;
                
                // Brand match
                if (brand === checkWord) score += 50;
                if (brand.includes(checkWord) && checkWord.length >= 3) score += 35;
                
                // Material match
                if (material.includes(checkWord) && checkWord.length >= 3) score += 25;
            });
            
            // Fuzzy matching for typos (only if word is 4+ chars)
            if (word.length >= 4) {
                const allTextWords = [
                    ...name.split(/[\s-_]+/),
                    ...category.split(/[\s-_]+/),
                    ...tags.flatMap(t => t.split(/[\s-_]+/)),
                    ...collections.flatMap(c => c.split(/[\s-_]+/))
                ];
                
                allTextWords.forEach(textWord => {
                    if (textWord.length >= 4 && fuzzyMatch(word, textWord, 2)) {
                        score += 30;
                    }
                });
            }
        });
        
        return score;
    };

    // Smart product search with ranking
    const smartProductSearch = useCallback((query) => {
        if (!query.trim() || !allProducts || allProducts.length === 0) {
            return [];
        }

        const queryLower = query.toLowerCase().trim();
        const scoredProducts = [];

        allProducts.forEach((product) => {
            const score = calculateRelevanceScore(product, queryLower);
            
            // CRITICAL FIX: Only include products with score > 0
            if (score > 0) {
                scoredProducts.push({
                    product,
                    score,
                    name: product.name || product.title || 'Unknown Product'
                });
            }
        });

        // Sort by relevance score (highest first)
        scoredProducts.sort((a, b) => b.score - a.score);

        // Get unique product names with their highest scores
        const uniqueProducts = [];
        const seenNames = new Set();

        scoredProducts.forEach(({ product, score, name }) => {
            const normalizedName = name.toLowerCase().trim();
            if (!seenNames.has(normalizedName)) {
                seenNames.add(normalizedName);
                uniqueProducts.push({
                    name,
                    score,
                    product
                });
            }
        });

        return uniqueProducts.slice(0, 8);
    }, [allProducts]);

    // Extract unique values from products for suggestions
    const extractUniqueValues = useCallback((field) => {
        if (!allProducts || allProducts.length === 0) return [];
        
        const values = new Set();
        allProducts.forEach(product => {
            if (Array.isArray(product[field])) {
                product[field].forEach(val => {
                    if (val) values.add(val);
                });
            } else if (product[field]) {
                values.add(product[field]);
            }
        });
        
        return Array.from(values);
    }, [allProducts]);

    // Generate trending searches dynamically
    const trendingSearches = React.useMemo(() => {
        const categories = extractUniqueValues('category');
        const tags = extractUniqueValues('tags').slice(0, 3);
        const collections = extractUniqueValues('collections').slice(0, 2);
        
        const trends = [
            ...categories.slice(0, 2),
            ...tags,
            ...collections,
            'oversized',
            'new arrivals'
        ];
        
        return [...new Set(trends)].slice(0, 6);
    }, [extractUniqueValues]);

    // Enhanced suggestion generation
    const fetchSuggestions = useCallback(async (query) => {
        if (!query.trim() || query.length < 2) {
            setSuggestions([]);
            return;
        }

        setLoading(true);
        
        try {
            await new Promise(resolve => setTimeout(resolve, 150));
            
            const queryLower = query.toLowerCase().trim();
            const queryWords = queryLower.split(/\s+/).filter(w => w.length > 0);
            
            // Get smart product matches
            const productMatches = smartProductSearch(query);
            
            // Create product suggestions with actual counts
            const productSuggestions = productMatches.map((match, index) => {
                // Count how many products actually match this search
                const matchCount = allProducts.filter(p => {
                    const score = calculateRelevanceScore(p, queryLower);
                    return score > 0;
                }).length;
                
                return {
                    id: `product-${index}`,
                    text: match.name,
                    type: 'product',
                    count: matchCount,
                    score: match.score
                };
            });

            // Generate smart variations based on query
            const variations = new Set();
            const categories = extractUniqueValues('category');
            const colors = extractUniqueValues('colors');
            const allTags = extractUniqueValues('tags');
            
            // Category-based variations
            categories.forEach(category => {
                const catLower = category.toLowerCase();
                const catWords = catLower.split(/\s+/);
                
                // Check if query matches any word in category
                const hasMatch = queryWords.some(qw => 
                    catWords.some(cw => cw.includes(qw) || qw.includes(cw))
                );
                
                if (hasMatch && catLower !== queryLower) {
                    variations.add(category);
                }
            });

            // Tag-based variations
            allTags.forEach(tag => {
                const tagLower = tag.toLowerCase();
                if (tagLower.includes(queryLower) && tagLower !== queryLower) {
                    variations.add(tag);
                }
            });

            // Color combinations
            colors.forEach(color => {
                const colorLower = color.toLowerCase();
                if (!queryLower.includes(colorLower) && queryWords.length > 0) {
                    // Add color to query
                    variations.add(`${color} ${query}`);
                }
            });

            // Style variations with synonyms
            queryWords.forEach(word => {
                const synonyms = getSynonyms(word);
                synonyms.slice(0, 2).forEach(syn => {
                    const varText = queryLower.replace(new RegExp(word, 'g'), syn);
                    if (varText !== queryLower) {
                        variations.add(varText);
                    }
                });
            });

            // Common style additions if not present
            const styleAdditions = ['oversized', 'loose fit'];
            styleAdditions.forEach(style => {
                if (!queryLower.includes(style) && queryWords.length > 0) {
                    variations.add(`${style} ${query}`);
                }
            });

            // Convert variations to suggestions
            const variationSuggestions = Array.from(variations)
                .slice(0, 4)
                .map((text, index) => ({
                    id: `variation-${index}`,
                    text: text,
                    type: 'variation',
                    count: Math.floor(Math.random() * 15) + 3
                }));

            // Combine all suggestions
            const allSuggestions = [
                ...productSuggestions.slice(0, 6),
                ...variationSuggestions.slice(0, 2)
            ].slice(0, 8);
            
            setSuggestions(allSuggestions);
            
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, [smartProductSearch, allProducts, extractUniqueValues]);

    // ===== END OF ENHANCED SEARCH SYSTEM =====

    // Load recent searches from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('recentSearches');
        if (saved) {
            try {
                setRecentSearches(JSON.parse(saved));
            } catch (error) {
                console.error('Error loading recent searches:', error);
            }
        }
    }, []);

    // Save recent searches to localStorage
    const saveToRecentSearches = useCallback((term) => {
        if (!term.trim()) return;
        
        const updated = [term, ...recentSearches.filter(item => item !== term)].slice(0, 5);
        setRecentSearches(updated);
        try {
            localStorage.setItem('recentSearches', JSON.stringify(updated));
        } catch (error) {
            console.error('Error saving recent searches:', error);
        }
    }, [recentSearches]);

    // Debounced search function
    const debouncedSearch = useCallback((query) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        
        debounceRef.current = setTimeout(() => {
            fetchSuggestions(query);
        }, 300);
    }, [fetchSuggestions]);

    // Handle input change
    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setSelectedIndex(-1);
        
        if (value.trim().length >= 1) {
            debouncedSearch(value);
            setShowSuggestions(true);
        } else {
            setSuggestions([]);
            setShowSuggestions(true);
        }
    };

    // Handle search toggle
    const handleSearchToggle = () => {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        
        if (newIsOpen) {
            setTimeout(() => {
                inputRef.current?.focus();
                if (!searchTerm.trim()) {
                    setShowSuggestions(true);
                }
            }, 100);
        } else {
            setShowSuggestions(false);
            setSearchTerm('');
            setSuggestions([]);
            setSelectedIndex(-1);
        }
    };

    // Handle search submission
    const handleSearch = (e, searchQuery = searchTerm) => {
        if (e) e.preventDefault();
        
        const finalQuery = searchQuery.trim();
        if (!finalQuery) return;
        
        saveToRecentSearches(finalQuery);
        dispatch(setFilters({ search: finalQuery }));
        dispatch(fetchProductsByFilters({ search: finalQuery }));
        navigate(`/collections/all?search=${encodeURIComponent(finalQuery)}`);
        
        setIsOpen(false);
        setShowSuggestions(false);
        setSearchTerm('');
        setSuggestions([]);
        setSelectedIndex(-1);
    };

    // Handle suggestion selection
    const handleSuggestionSelect = (suggestionText) => {
        if (!suggestionText.trim()) return;
        
        setSearchTerm(suggestionText);
        setShowSuggestions(false);
        handleSearch(null, suggestionText);
    };

    // Get all suggestions for keyboard navigation
    const getAllSuggestions = useCallback(() => {
        const allSuggestions = [];
        
        if (searchTerm.trim().length > 0) {
            allSuggestions.push(...suggestions.map(s => s.text));
        } else {
            allSuggestions.push(...recentSearches);
            allSuggestions.push(...trendingSearches);
        }
        
        return allSuggestions;
    }, [suggestions, recentSearches, trendingSearches, searchTerm]);

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!showSuggestions) return;
        
        const allSuggestions = getAllSuggestions();
        const totalItems = allSuggestions.length;
        
        if (totalItems === 0) return;
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev + 1) % totalItems);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
        } else if (e.key === 'Enter' && selectedIndex >= 0 && selectedIndex < totalItems) {
            e.preventDefault();
            handleSuggestionSelect(allSuggestions[selectedIndex]);
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setSelectedIndex(-1);
        }
    };

    // Handle clicking outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowSuggestions(false);
                setSelectedIndex(-1);
            }
        };

        if (showSuggestions) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showSuggestions]);

    // Clean up debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    // Render suggestion item
    const renderSuggestionItem = (text, index, icon, label) => (
        <div
            key={`suggestion-${index}-${text}`}
            className={`flex items-center px-4 py-3 cursor-pointer transition-colors ${
                selectedIndex === index ? 'bg-gray-100' : 'hover:bg-gray-50'
            }`}
            onClick={() => handleSuggestionSelect(text)}
        >
            <div className="mr-3 text-gray-400">
                {icon}
            </div>
            <div className="flex-1">
                <span className="text-gray-900">{text}</span>
            </div>
            {label && (
                <span className="text-xs text-gray-500 ml-2">{label}</span>
            )}
        </div>
    );

    let currentIndex = 0;

    return (
        <div className={`flex items-center justify-center w-full transition-all duration-300 
            ${isOpen ? "absolute top-20 left-0 w-full bg-white h-auto z-50" : "w-auto"}`}>
            
            {isOpen ? (
                <div className="relative flex flex-col items-center justify-center w-full">
                    <form onSubmit={handleSearch} className="relative flex items-center justify-center w-full py-6 px-4 sm:px-6">
                        <div className="relative w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-4 sm:mx-0" ref={suggestionsRef}>
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Search products, colors, styles..."
                                value={searchTerm}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                onFocus={() => setShowSuggestions(true)}
                                className="bg-gray-100 px-4 py-3 pl-4 pr-12 rounded-lg focus:outline-none focus:ring-2 focus:ring-black w-full
                                    placeholder:text-gray-500 text-gray-900 text-sm sm:text-base"
                                autoComplete="off"
                            />
                            
                            <button
                                type="submit"
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-200 p-2 rounded-full hover:bg-gray-300 transition-colors
                                    focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
                            >
                                <Search className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>

                            {showSuggestions && (
                                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-96 overflow-y-auto z-50">
                                    {loading && (
                                        <div className="flex items-center justify-center py-4">
                                            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                                        </div>
                                    )}

                                    {!loading && (
                                        <>
                                            {suggestions.length > 0 && (
                                                <div className="border-b border-gray-100">
                                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                        Suggestions
                                                    </div>
                                                    {suggestions.map((suggestion) => {
                                                        const item = renderSuggestionItem(
                                                            suggestion.text, 
                                                            currentIndex, 
                                                            <Search className="h-4 w-4" />,
                                                            `${suggestion.count} item${suggestion.count !== 1 ? 's' : ''}`
                                                        );
                                                        currentIndex++;
                                                        return item;
                                                    })}
                                                </div>
                                            )}

                                            {recentSearches.length > 0 && searchTerm.length === 0 && (
                                                <div className="border-b border-gray-100">
                                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                        Recent Searches
                                                    </div>
                                                    {recentSearches.map((recent) => {
                                                        const item = renderSuggestionItem(
                                                            recent, 
                                                            currentIndex, 
                                                            <Clock className="h-4 w-4" />
                                                        );
                                                        currentIndex++;
                                                        return item;
                                                    })}
                                                </div>
                                            )}

                                            {searchTerm.length === 0 && (
                                                <div>
                                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                        Trending
                                                    </div>
                                                    {trendingSearches.map((trending) => {
                                                        const item = renderSuggestionItem(
                                                            trending, 
                                                            currentIndex, 
                                                            <TrendingUp className="h-4 w-4" />
                                                        );
                                                        currentIndex++;
                                                        return item;
                                                    })}
                                                </div>
                                            )}

                                            {suggestions.length === 0 && searchTerm.length > 0 && !loading && (
                                                <div className="px-4 py-6 text-center text-gray-500">
                                                    No suggestions found for "{searchTerm}"
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <button
                            type="button"
                            className="absolute right-10 top-1/2 transform -translate-y-1/2 bg-gray-200 p-2 rounded-full hover:bg-gray-300 transition-colors"
                            onClick={handleSearchToggle}
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </form>
                </div>
            ) : (
                <button onClick={handleSearchToggle}>
                    <Search className="h-6 w-6" />
                </button>
            )}
        </div>
    );
};

export default Searchbar;
